/* eslint-disable react/prop-types */ // TODO: add props
import { useMemo, useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { useExpanded, useSortBy, useTable } from 'react-table-7';
import style from './ResultsTable.scss';

export const ResultsTable = ({ data, columns, highlightProfileId }) => {
  const defaultExpandedRows = data.reduce((acc, item, index) => {
    if (
      highlightProfileId === data.get(index).get('userProfileId') &&
      data.get(index).get('answer')
    ) {
      return { ...acc, [index]: true };
    }
    return acc;
  }, {});

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        data,
        columns,
        expandSubRows: false,
        disableSortRemove: true,
        initialState: {
          sortBy: useMemo(() => [{ id: 'name' }], []),
          expanded: defaultExpandedRows,
        },
      },
      useSortBy,
      useExpanded
    );

  const [shouldHighlight, setShouldHighlight] = useState(true);
  const highlightedElement = useRef(null);
  const handleClickOutsideHighlight = useRef(null);

  useEffect(() => {
    const node = highlightedElement.current;

    if (node !== null && shouldHighlight) {
      node.scrollIntoView({
        block: 'center',
        inline: 'center',
      });

      handleClickOutsideHighlight.current = (e) => {
        if (!node.contains(e.target)) {
          setShouldHighlight(false);
        }
      };

      // Add overlay
      const overlay = document.createElement('div');
      overlay.classList.add(cx(style.overlay));
      overlay.id = 'overlay';
      document.body.appendChild(overlay);

      // Disables scrolling
      document.body.classList.add(cx(style.body));

      // Detects click outside of highlighted region
      window.addEventListener('mousedown', handleClickOutsideHighlight.current);
    }

    return () => {
      // Remove overlay
      const overlay = document.getElementById('overlay');
      if (overlay) {
        document.body.removeChild(overlay);
      }

      // Enable scrolling
      document.body.classList.remove(cx(style.body));
      window.removeEventListener(
        'mousedown',
        handleClickOutsideHighlight.current
      );
    };
  }, [shouldHighlight, highlightedElement, handleClickOutsideHighlight]);

  return (
    <>
      <table className={style.table} {...getTableProps()}>
        <thead className={style.tableHeader}>
          {headerGroups.map((headerGroup, i) => (
            <tr
              className={style.tableRow}
              {...headerGroup.getHeaderGroupProps()}
              key={i}
            >
              {headerGroup.headers.map((column, i) => (
                <th
                  className={style.tableHeaderCell}
                  key={i}
                  scope="col"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={column.style}
                >
                  <div className={style.tableHeaderTextWrapper}>
                    {column.render('Header')}
                    {column.isSorted ? (
                      <span
                        className={column.isSortedDesc ? style.desc : style.asc}
                      />
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody className={style.tableBody} {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            const rowIsHighlighted =
              row.original.get('userProfileId') === highlightProfileId &&
              shouldHighlight;

            return (
              <tr
                className={cx(style.tableRow, {
                  [style.tableRowHighlighted]: rowIsHighlighted,
                })}
                {...row.getRowProps()}
                key={i}
                ref={rowIsHighlighted ? highlightedElement : null}
              >
                {row.cells.map((cell, i) => {
                  const isNameColumn =
                    cell.column.id === 'name' || cell.column.index === 0;
                  return (
                    <td
                      className={style.tableBodyCell}
                      {...cell.getCellProps()}
                      key={i}
                    >
                      {isNameColumn ? (
                        <span data-mp-mask>{cell.render('Cell')}</span>
                      ) : (
                        cell.render('Cell')
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};
